import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border pl-1 pr-2 py-0.5 text-xs gap-1.5 font-semibold transition-colors focus:outline-hidden focus:ring-2  focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-slate-4 dark:bg-slatedark-4 text-slate-normal",
        secondary: "border-transparent bg-slate-action",
        destructive:
          "border-transparent bg-red-4 dark:bg-reddark-4 text-red-normal hover:bg-red-10 dark:hover:bg-reddark-10",
        warning:
          "border-transparent bg-yellow-9 dark:bg-yellowdarka-7 text-yellow-normal",
        success:
          "border-transparent text-green-normal bg-green-6 dark:bg-greendarka-6",
        outline: "text-slate-normal",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

interface BadgeProps
  extends React.ComponentProps<"div">,
    VariantProps<typeof badgeVariants> {
  noIcon?: boolean;
  /**
   * Render the badge as its child element. Use this when the badge is also an
   * interactive trigger: a Radix trigger spreads type/aria-expanded onto it,
   * and those are invalid on a plain div.
   */
  asChild?: boolean;
}

function Badge({
  className,
  variant,
  noIcon,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      className={cn(badgeVariants({ variant }), noIcon && "pl-2", className)}
      {...props}
    />
  );
}

export { Badge };
